import type {PropertyValue, ValueType} from '@github-ui/custom-properties-types'
import {FormControl} from '@primer/react'

import {CustomPropertyInput} from '../CustomPropertyInput'
import styles from './PropertyValueField.module.css'

interface Props {
  // Used to create accessibility ids for the form field and its descriptors
  idPrefix: string
  valueType: ValueType
  value: PropertyValue
  label: string
  caption?: string
  error?: string
  allowedValues: string[] | null
  propertyName: string
  onChange: (value: PropertyValue) => void
  setInputRef?: (ref: HTMLElement | null) => void
  className?: string
  required?: boolean
}

export function PropertyValueField({
  idPrefix,
  label,
  caption,
  valueType,
  value,
  error,
  allowedValues,
  propertyName,
  onChange,
  setInputRef,
  className,
  required,
}: Props) {
  const isText = valueType === 'string'
  const isTrueFalse = valueType === 'true_false'
  const isUrl = valueType === 'url'

  const labelId = `${idPrefix}-label`
  const validationId = `${idPrefix}-validation`
  const captionId = `${idPrefix}-caption`

  const invalid = !!error
  const describedBy = [invalid ? validationId : '', caption ? captionId : ''].filter(Boolean).join(' ') || undefined
  const ariaProps = {
    'aria-invalid': invalid,
    // The input is labelled by the FormControl.Label via aria-labelledby, so the
    // default aria-label from CustomPropertyInput must be cleared to avoid having both.
    'aria-label': undefined,
    'aria-labelledby': labelId,
    'aria-describedby': describedBy,
  }

  const allowableValues = (isTrueFalse ? ['true', 'false'] : allowedValues) || null
  return (
    <FormControl required={required} className={className}>
      <FormControl.Label id={labelId}>{label}</FormControl.Label>

      <div className={isText || isUrl ? 'width-full' : styles.selectPanelContainer}>
        <CustomPropertyInput
          propertyName={propertyName}
          setRef={setInputRef}
          propertyValue={value}
          onChange={onChange}
          valueType={valueType}
          allowedValues={allowableValues}
          anchorProps={ariaProps}
          inputProps={ariaProps}
          defaultValue={null}
          mixed={false}
        />
      </div>

      {error && (
        <FormControl.Validation id={validationId} variant="error" className={styles.validationFeedback}>
          {error}
        </FormControl.Validation>
      )}

      {caption && <FormControl.Caption id={captionId}>{caption}</FormControl.Caption>}
    </FormControl>
  )
}
